import React, { useState, useEffect } from "react";
import { Card, Button, Spinner, Alert, Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import { Link } from "react-router-dom";
import { format } from 'date-fns';
import "bootstrap/dist/css/bootstrap.min.css";
import "./BlogSection.css";

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    let isMounted = true;

    const fetchBlogs = async () => {
      try {
        const response = await axios.get("https://agpipefitting.org.et/api/blogs", { signal });
        
        if (isMounted) {
          if (response.data && Array.isArray(response.data)) {
            // Validate and filter blog data
            const validBlogs = response.data.filter(blog => 
              blog?.id &&
              blog?.title &&
              blog?.content &&
              blog?.status?.toLowerCase() === "published"
            );

            // Sort by date if available
            const sortedBlogs = validBlogs.sort((a, b) => 
              new Date(b.created_at || Date.now()) - new Date(a.created_at || Date.now())
            );

            setBlogs(sortedBlogs);
          } else {
            throw new Error("Invalid data format received from server");
          }
        }
      } catch (err) {
        console.error("API Error:", err.message);
        if (isMounted && !axios.isCancel(err)) {
          setError("Failed to load blog posts. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchBlogs();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  if (loading) {
    return (
      <div className="loading-container d-flex align-items-center justify-content-center py-5">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <span className="ms-3 text-primary">Loading Fresh Content...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container text-center py-5">
        <Alert variant="danger" className="mx-auto alert-rounded" style={{ maxWidth: "500px" }}>
          <h5 className="mb-0">{error}</h5>
        </Alert>
      </div>
    );
  }

  if (blogs.length === 0) {
    return (
      <div className="text-center py-5">
        <Alert variant="info" className="mx-auto" style={{ maxWidth: "500px" }}>
          <h5 className="mb-0">No blog posts available. Please check back later!</h5>
        </Alert>
      </div>
    );
  }

  return (
    <section id="blog" className="py-5 bg-light">
      <Container>
        <div className="text-center mb-5">
          <h2 className="display-5 fw-bold text-dark mb-3">
            Latest Insights & Stories
          </h2>
          <p className="lead text-muted">
            Discover our latest articles, news, and professional insights
          </p>
        </div>

        <Row className="g-4 justify-content-center">
          {blogs.slice(0, 8).map((post) => {
            const imageUrl = post.image
              ? `https://agpipefitting.org.et/storage/${post.image}`
              : 'https://via.placeholder.com/400x300.png?text=Blog+Image';

            const postDate = post.created_at 
              ? format(new Date(post.created_at), 'MMM dd, yyyy')
              : '';

            return (
              <Col key={`blog-${post.id}`} xs={12} md={6} lg={4} xl={3}>
                <Card className="blog-card shadow-sm border-0 rounded-4 h-100 overflow-hidden transition-all">
                  <div className="card-img-container position-relative">
                    <Card.Img
                      variant="top"
                      src={imageUrl}
                      className="card-img-top img-fluid"
                      alt={post.title}
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/400x300.png?text=Blog+Image";
                      }}
                    />
                    {postDate && (
                      <div className="card-date position-absolute bottom-0 start-0 bg-primary text-white p-2">
                        {postDate}
                      </div>
                    )}
                  </div>
                  <Card.Body className="d-flex flex-column justify-content-between p-4">
                    <div>
                      <Card.Title className="blog-card-title fw-semibold mb-3 line-clamp-2">
                        {post.title}
                      </Card.Title>
                     
                    </div>
                    <Link to={`/blogs/${post.id}`} className="text-decoration-none">
                      <Button 
                        variant="outline-primary" 
                        className="btn-rounded w-100 d-flex align-items-center justify-content-center gap-2"
                      >
                        Read More
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-right" viewBox="0 0 16 16">
                          <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8"/>
                        </svg>
                      </Button>
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {blogs.length > 8 && (
          <div className="text-center mt-5">
            <Link to="/blogs">
              <Button 
                variant="primary" 
                className="btn-rounded px-4 py-2 d-inline-flex align-items-center gap-2"
              >
                View All Posts
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-arrow-right-circle" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8m15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0M4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5z"/>
                </svg>
              </Button>
            </Link>
          </div>
        )}
      </Container>
    </section>
  );
};

export default BlogSection;